import { Check, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { ROLE_LABEL, type Role } from "@/shared/utils/roles";
import { CAPABILITY_COLUMNS, capabilityCell, ROLE_CAPABILITIES } from "./role-capabilities";

/**
 * A matriz do que cada cargo pode fazer, aberta pelo formulário de criar
 * usuário. A coluna do cargo escolhido no formulário vem em destaque, e
 * clicar no cabeçalho de um cargo que quem convida pode dar
 * (`selectable`) troca o Cargo do formulário. Os outros cabeçalhos são
 * só rótulo: o Administrador vê a coluna do Máster, mas não a escolhe.
 * Fecha por X, Esc e clique fora, como os outros modais.
 */
export function RoleCapabilitiesDialog({
  selected,
  selectable = [],
  onSelect,
  onClose
}: {
  selected?: Role;
  selectable?: readonly Role[];
  onSelect?: (role: Role) => void;
  onClose: () => void;
}) {
  useDismiss(true, () => onClose());

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container role-capabilities"
        role="dialog"
        aria-modal="true"
        aria-label="O que cada cargo pode fazer"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <h2>O que cada cargo pode fazer</h2>
            <p className="role-capabilities-lead">
              Cada cargo herda tudo dos cargos à esquerda.{onSelect && selectable.length > 0 && " Clique num cargo para escolhê-lo no formulário."}
            </p>
          </div>
          <button type="button" className="icon-button" aria-label="Fechar" onClick={onClose}>
            <X size={16} aria-hidden="true"/>
          </button>
        </header>
        <div className="modal-body">
          <div className="role-capabilities-scroll">
            <table className="role-capabilities-table">
              <thead>
                <tr>
                  <th scope="col"><span className="sr-only">O que dá pra fazer</span></th>
                  {CAPABILITY_COLUMNS.map(role => (
                    <th key={role} scope="col" className={role === selected ? "is-selected" : undefined}>
                      {onSelect && selectable.includes(role) ? (
                        <button
                          type="button"
                          className="role-chip"
                          aria-pressed={role === selected}
                          onClick={() => onSelect(role)}
                        >
                          {ROLE_LABEL[role]}
                        </button>
                      ) : (
                        <span className="role-chip">{ROLE_LABEL[role]}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              {ROLE_CAPABILITIES.map(section => (
                <tbody key={section.title}>
                  <tr className="role-capabilities-section">
                    <th scope="colgroup" colSpan={CAPABILITY_COLUMNS.length + 1}>{section.title}</th>
                  </tr>
                  {section.rows.map(row => (
                    <tr key={row.label}>
                      <th scope="row">{row.label}</th>
                      {CAPABILITY_COLUMNS.map(role => {
                        const cell = capabilityCell(row, role);
                        const label = `${ROLE_LABEL[role]}: ${cell.kind === "yes" ? "pode" : cell.kind === "no" ? "não pode" : cell.text}`;
                        return (
                          <td key={role} className={role === selected ? "is-selected" : undefined} aria-label={label}>
                            {cell.kind === "yes" && <Check size={18} className="role-cap-yes" aria-hidden="true"/>}
                            {/* X e não traço: o traço lia como célula em branco. O tom
                                continua `--muted` (um "não" não é erro). */}
                            {cell.kind === "no" && <X size={16} className="role-cap-no" aria-hidden="true"/>}
                            {cell.kind === "note" && <span className="role-cap-note" aria-hidden="true">{cell.text}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
          <p className="role-capabilities-foot">
            “Com chave”: o Máster libera a edição de prompt e de modelo empresa por empresa, em Ajustes › Texto do prompt e Modelo de IA.
            “Cargos abaixo”: o Administrador gere só gente do núcleo dele, nos cargos abaixo do dele.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
